import { ChangeEventHandler, FC, useEffect, useMemo, useRef, useState } from 'react';

import * as Sentry from '@sentry/browser';

import { useAnalytics } from '@hooks/useAnalytics';
import { useDevice } from '@hooks/useDevice';

import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';

interface VideoUploadFieldProps {
  name?: string;
  placeholder: string;
  defaultValue?: string;
  onChange: (file: File) => void;
}

const NATIVE_IMAGE_ENABLED = false;

function generateVideoThumbnail(file: File) {
  return new Promise<Blob>((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');

    // Create URL for video blob
    const url = URL.createObjectURL(file);
    video.src = url;
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    video.addEventListener('loadeddata', () => {
      // Seek to 1 second to avoid black frames at 0
      video.currentTime = 1;
    });

    video.addEventListener('seeked', () => {
      // Set canvas dimensions to video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to base64 image or blob
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url); // Clean up
        resolve(blob);
      }, 'image/jpeg');
    });

    video.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject('Failed to load video for preview');
    };
  });
}

const readFile = (file: Blob): Promise<string | ArrayBuffer> => {
  return new Promise<string | ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = (error) => {
      Sentry.captureException(error);
      console.log('Error: ', error);
      reject(error);
    };
  });
};

export const VideoUploadField: FC<VideoUploadFieldProps> = ({
  name,
  placeholder,
  defaultValue,
  onChange,
}) => {
  const { isNativePlatform } = useDevice();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<Blob | MediaSource>();
  const [previewUrl, setPreviewUrl] = useState<string>(defaultValue);

  // create a preview as a side effect, whenever selected file is changed
  useEffect(() => {
    if (!selectedFile) {
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const handleClick = async () => {
    if (isNativePlatform() && NATIVE_IMAGE_ENABLED) {
      //   try {
      //     const image = await Camera.pickImages({
      //       mediaType: 'video',
      //       resultType: CameraResultType.Uri, // more efficient than Base64
      //     });
      //     const response = await fetch(image.webPath);
      //     const blob = await response.blob();
      //     const base64String = await readFile(blob);
      //     setSelectedFile(blob);
      //     onChange(base64String, blob);
      //   } catch (error) {
      //     inputRef.current?.click();
      //   }
    } else {
      inputRef.current?.click();
    }
  };

  const handleChangeFile: ChangeEventHandler<HTMLInputElement> = async (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedFile(undefined);
      return;
    }

    const firstFile = e.target.files[0];
    setSelectedFile(firstFile);
    onChange(firstFile);

    try {
      const previewBlob = await generateVideoThumbnail(firstFile);
      setPreviewUrl(URL.createObjectURL(previewBlob));
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  return (
    <div onClick={handleClick} style={{ minHeight: 32 }}>
      {previewUrl ? (
        <img className="d-block" src={previewUrl} />
      ) : (
        <div className="d-flex flex-column align-items-center py-5 px-2">
          <div className="fs-1">
            <FontAwesomeIcon name="video" />
          </div>
          <span>{placeholder}</span>
        </div>
      )}
      <div className="d-none">
        <input
          ref={inputRef}
          type="file"
          name={name}
          accept={`video/*,.mov`}
          onChange={handleChangeFile}
        />
      </div>
    </div>
  );
};
